import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Avatar,
  Button,
  EmptyState,
  KanbanBoard,
  KanbanCard,
  KanbanColumn,
  PageHeader,
  Tag,
  useToast,
} from '@/design/components'
import type { KanbanAccent } from '@/design/components'
import { useMe } from '@/app/MeContextProvider'
import { ApiError } from '@/lib/api'
import { daysUntil, formatRelative, plural } from '@/lib/format'
import { SOURCE_LABEL, STAGE_LABEL, STAGE_ORDER, nextStage } from '../labels'
import { useBoardColumn, useRouteDocument } from '../hooks'
import type { DocumentRow, RoutingStage } from '../types'
import { RouteModal } from '../components/RouteModal'

const ACCENTS: Record<RoutingStage, KanbanAccent> = {
  inbox: 'gold',
  partner_review: 'navy',
  assigned: 'green',
}

/** Podpis czasu na karcie — jak na mockupie („wpływ 2 godz.", „przekazano wczoraj"). */
function timeLabel(row: DocumentRow, stage: RoutingStage): string {
  if (stage === 'inbox') return `wpływ ${formatRelative(row.received_at).replace(' temu', '')}`
  if (stage === 'partner_review') {
    return `u partnera ${formatRelative(row.routed_at ?? row.received_at).replace(' temu', '')}`
  }
  return `przekazano ${formatRelative(row.routed_at ?? row.received_at).replace(' temu', '')}`
}

/**
 * Osoba w stopce karty = adresat dekretacji (`routed_to`).
 *
 * Wcześniej stał tu hardkodowany awatar „SK · Sekretariat" pokazywany na KAŻDEJ karcie
 * bez adresata — niezależnie od tego, kto dokument wgrał. API dokumentu nie zwraca
 * autora wgrania (`DocumentRow` ma tylko `routed_to`), więc dla pisma jeszcze
 * nieprzekazanego pokazujemy KANAŁ WPŁYWU zamiast zmyślonej osoby.
 */
function person(row: DocumentRow) {
  if (row.routed_to) {
    return (
      <>
        <Avatar name={row.routed_to.full_name} size="sm" tone="light" />
        <span className="truncate">{row.routed_to.full_name}</span>
      </>
    )
  }
  return <span className="truncate text-muted">{SOURCE_LABEL[row.source] ?? 'nieprzekazany'}</span>
}

function isOverdue(row: DocumentRow): boolean {
  const days = daysUntil(row.received_at)
  return days !== null && days <= -2
}

export function RoutingPage() {
  const { can } = useMe()
  const navigate = useNavigate()
  const toast = useToast()
  const route = useRouteDocument()
  const [selected, setSelected] = useState<DocumentRow | null>(null)

  const columns = {
    inbox: useBoardColumn('inbox'),
    partner_review: useBoardColumn('partner_review'),
    assigned: useBoardColumn('assigned'),
  }

  const canRoute = can('documents.route')

  /** Mobilne „Zatwierdź" = przesuń na kolejny etap bez wyboru osoby. */
  const approve = async (row: DocumentRow) => {
    const target = nextStage(row.routing_stage)
    if (!target) return
    if (target === 'assigned') {
      setSelected(row)
      return
    }
    try {
      await route.mutateAsync({ id: row.id, stage: target })
      toast.success(`Przekazano: ${STAGE_LABEL[target]}`)
    } catch (error) {
      toast.error(error instanceof ApiError ? error.userMessage : 'Nie udało się przekazać.')
    }
  }

  return (
    <>
      <PageHeader
        title="Dekretacja i akceptacja"
        subtitle="Kto zatwierdza dokument i komu go przekazuje. Kliknij kartę, aby otworzyć pismo; „Przekaż dalej” wskazuje kolejny etap i osobę."
      />

      <KanbanBoard>
        {STAGE_ORDER.map((stage) => {
          const query = columns[stage]
          const rows = query.data?.items ?? []
          return (
            <KanbanColumn
              key={stage}
              title={STAGE_LABEL[stage]}
              accent={ACCENTS[stage]}
              meta={
                query.isPending
                  ? '…'
                  : `${rows.length} ${plural(rows.length, 'dokument', 'dokumenty', 'dokumentów')}`
              }
            >
              {rows.length === 0 && !query.isPending ? (
                <EmptyState title="Brak dokumentów" />
              ) : null}
              {rows.map((row) => (
                <div key={row.id} className="flex flex-col gap-2">
                  <KanbanCard
                    tag={row.doc_type ? <Tag>{row.doc_type}</Tag> : undefined}
                    signature={row.case?.signature ?? row.suggested_case?.signature ?? 'brak'}
                    title={row.title}
                    urgent={isOverdue(row)}
                    footerLeft={person(row)}
                    footerRight={timeLabel(row, stage)}
                    onClick={() => navigate(`/dokumenty/weryfikacja/${row.id}`)}
                  />
                  {canRoute && stage !== 'assigned' ? (
                    <div className="grid grid-cols-2 gap-2 shell:hidden">
                      <Button variant="primary" size="lg" onClick={() => void approve(row)}>
                        Zatwierdź
                      </Button>
                      <Button variant="secondary" size="lg" onClick={() => setSelected(row)}>
                        Przekaż
                      </Button>
                    </div>
                  ) : null}
                  {canRoute && stage !== 'assigned' ? (
                    // Tablica WYGLĄDA jak kanban, ale przeciągania kart nie ma — jedyne
                    // przejście między kolumnami musi więc być widocznym przyciskiem,
                    // a nie szarym linkiem pod kartą.
                    <Button
                      variant="secondary"
                      block
                      className="hidden shell:inline-flex"
                      onClick={() => setSelected(row)}
                    >
                      Przekaż dalej →
                    </Button>
                  ) : null}
                </div>
              ))}
            </KanbanColumn>
          )
        })}
      </KanbanBoard>

      <RouteModal document={selected} onClose={() => setSelected(null)} />
    </>
  )
}
